import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  IndianRupee,
  Scale,
  ShieldCheck,
  Sparkles,
  User,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useProfile } from "../context/ProfileContext";
import { Card, CardContent } from "../components/ui/card";
import { SumAssuredChart } from "../components/SumAssuredChart";
import { PortfolioGrowthCard } from "../components/PortfolioGrowthCard";
import { fetchNetWorth, fetchPoliciesSummary } from "../lib/api";

function formatINR(n) {
  if (n == null || Number.isNaN(Number(n))) return null;
  return new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n));
}

/**
 * Member dashboard (`/app`) — the landing view after OTP login.
 * Shows greeting, net-worth strip, portfolio growth, sum-assured chart, and profile /
 * policies / net-worth summary cards. Calculators live entirely on the dedicated
 * `/app/calculators` route (surfaced via nav) — they're intentionally not duplicated here.
 */
export function Home() {
  const { member } = useAuth();
  const { profile, loading: profileLoading } = useProfile();

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:py-10">
      <Greeting name={profile?.name} email={member?.email} />

      <NetWorthStrip />

      <div className="mt-8">
        <PortfolioGrowthCard />
      </div>

      <div className="mt-8">
        <SumAssuredChart />
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-3">
        <ProfileSnapshot profile={profile} loading={profileLoading} email={member?.email} />
        <PoliciesCard />
        <NetWorthTeaserCard />
      </div>
    </div>
  );
}

/* ---------------------------------- Greeting ---------------------------------- */

function Greeting({ name, email }) {
  const displayName = name || (email ? email.split("@")[0] : "there");
  const hour = new Date().getHours();
  const salutation = hour < 5 ? "Namaste" : hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="flex flex-col gap-2">
      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
        <span className="h-px w-6 bg-gold/60" />
        Your gurukul
      </div>
      <h1 className="font-serif text-3xl text-text sm:text-4xl">
        {salutation}, <span className="text-gold">{displayName}</span>.
      </h1>
      <p className="text-sm text-muted">
        Pick up where you left off — compute a plan, review a policy, or upload one for analysis.
      </p>
    </div>
  );
}

/* --------------------------------- Snapshot card -------------------------------- */

function ProfileSnapshot({ profile, loading, email }) {
  const complete = !!profile && !!profile.pan && !!profile.monthly_income_inr;
  return (
    <Card className="group relative overflow-hidden border-border transition-colors hover:border-gold/40" data-testid="dashboard-profile-card">
      <CardContent className="flex h-full flex-col gap-4 p-6">
        <SectionHeading icon={User} label="Profile" />
        {loading ? (
          <p className="text-sm text-muted">Loading profile…</p>
        ) : profile ? (
          <div className="flex flex-1 flex-col gap-3 text-sm">
            <SnapshotRow label="Name" value={profile.name} />
            <SnapshotRow label="Email" value={email || "—"} muted />
            <SnapshotRow label="Mobile" value={profile.mobile} muted />
            <SnapshotRow label="PAN" value={profile.pan || "Not added"} accent={!!profile.pan} />
            <SnapshotRow
              label="Monthly income"
              value={
                profile.monthly_income_inr != null
                  ? `₹ ${formatINR(profile.monthly_income_inr)}`
                  : "Not added"
              }
              accent={profile.monthly_income_inr != null}
            />
          </div>
        ) : (
          <div className="flex flex-1 flex-col items-start gap-2">
            <p className="text-sm text-muted">
              You haven&apos;t created a profile yet — takes about a minute and unlocks personalised
              calculators.
            </p>
          </div>
        )}
        <Link
          to="/profile"
          data-testid="dashboard-profile-edit"
          className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
        >
          {profile ? (complete ? "Edit profile" : "Complete your profile") : "Create profile"}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

function SnapshotRow({ label, value, muted = false, accent = false }) {
  return (
    <div className="flex items-center justify-between border-b border-border/60 pb-2 last:border-b-0 last:pb-0">
      <span className="text-xs uppercase tracking-widest text-muted">{label}</span>
      <span className={accent ? "font-medium text-gold" : muted ? "text-muted" : "text-text"}>
        {value}
      </span>
    </div>
  );
}

/* ----------------------------- Policies teaser card ----------------------------- */

const TYPE_LABEL = {
  term_life: "Term life",
  life_insurance: "Life insurance",
  health: "Health",
  accident: "Accident",
  two_wheeler: "2-Wheeler",
  four_wheeler: "4-Wheeler",
  other: "Other",
};

/* ------------------------------ Net Worth strip ------------------------------ */

function NetWorthTile({ label, value, subtle = false, testId, tone = "default", to }) {
  const toneClass =
    tone === "gold"
      ? "border-gold/40 bg-gold/5 text-gold"
      : tone === "danger"
      ? "border-red-500/30 bg-red-500/5 text-red-300"
      : "border-border bg-elevated/40 text-text";
  const body = (
    <>
      <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
      <div className={`mt-1 ${subtle ? "text-xl" : "text-2xl"} font-semibold`}>{value}</div>
    </>
  );
  // When `to` is supplied, render the tile as a Link so the whole card is a click target.
  // Preserves keyboard nav (Link is an <a>) and shows a subtle hover cue.
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`block rounded-lg border p-4 transition-colors hover:border-gold/50 ${toneClass}`}
      >
        {body}
      </Link>
    );
  }
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`} data-testid={testId}>
      {body}
    </div>
  );
}

function NetWorthStrip() {
  const [nw, setNw] = useState(null);
  useEffect(() => {
    fetchNetWorth()
      .then(setNw)
      .catch(() => setNw(null));
  }, []);

  const totalSA = nw?.total_sum_assured ?? 0;
  return (
    <section className="mt-8" data-testid="dashboard-networth">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gold/40 bg-gold/10 text-gold">
            <Scale className="h-4 w-4" />
          </div>
          <div>
            <div className="font-serif text-lg text-text">Net Worth</div>
            <div className="text-xs text-muted">
              Auto-updated using master rates. Overrides win where you&apos;ve set them.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/app/budget"
            data-testid="dashboard-budget-cta"
            className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
          >
            Monthly budget
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/app/net-worth"
            data-testid="dashboard-networth-cta"
            className="inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
          >
            Manage
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <NetWorthTile
          label="Net worth"
          value={`₹ ${formatINR(nw?.net_worth ?? 0)}`}
          tone="gold"
          testId="home-nw-total"
        />
        <NetWorthTile
          label={`Assets (${nw?.asset_count ?? 0})`}
          value={`₹ ${formatINR(nw?.total_assets ?? 0)}`}
          testId="home-nw-assets"
          to="/app/net-worth?tab=assets"
        />
        <NetWorthTile
          label={`Liabilities (${nw?.liability_count ?? 0})`}
          value={`₹ ${formatINR(nw?.total_liabilities ?? 0)}`}
          tone="danger"
          testId="home-nw-liabilities"
          to="/app/net-worth?tab=liabilities"
        />
        <NetWorthTile
          label="Sum assured (all policies)"
          value={totalSA > 0 ? `₹ ${formatINR(totalSA)}` : "—"}
          subtle
          testId="home-nw-sum-assured"
        />
      </div>
    </section>
  );
}

function PoliciesCard() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    fetchPoliciesSummary()
      .then(setSummary)
      .catch(() => setSummary({ total: 0, by_type: {}, renewals_due_soon: 0 }));
  }, []);

  const total = summary?.total ?? null;
  const dueSoon = summary?.renewals_due_soon ?? 0;
  const byType = summary?.by_type || {};

  return (
    <Card
      className="relative overflow-hidden border-border transition-colors hover:border-gold/40"
      data-testid="dashboard-policies-card"
    >
      <CardContent className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-start justify-between gap-3">
          <SectionHeading icon={ShieldCheck} label="My Policies" />
          {total != null && (
            <span className="rounded-full bg-elevated px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-muted">
              {total} total
            </span>
          )}
        </div>

        {total === 0 ? (
          <p className="flex-1 text-sm text-muted">
            Track your term life, health, motor and accident policies in one place — sum assured,
            renewal dates, reminders and the premium payment calendar.
          </p>
        ) : total == null ? (
          <p className="flex-1 text-sm text-muted">Loading…</p>
        ) : (
          <div className="flex-1 space-y-3">
            {dueSoon > 0 && (
              <div
                className="flex items-center gap-2 rounded-md border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200"
                data-testid="dashboard-policies-renewal-alert"
              >
                <Sparkles className="h-3.5 w-3.5" />
                <span>
                  <strong>{dueSoon}</strong> renewal{dueSoon > 1 ? "s" : ""} due within 30 days
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-xs">
              {Object.entries(byType).map(([t, count]) => (
                <div
                  key={t}
                  className="flex items-center justify-between rounded-md border border-border bg-elevated/40 px-3 py-1.5"
                >
                  <span className="uppercase tracking-widest text-muted">{TYPE_LABEL[t] || t}</span>
                  <span className="font-medium text-gold">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <Link
          to="/app/policies"
          data-testid="dashboard-policies-cta"
          className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
        >
          {total === 0 ? "Add your first policy" : "Manage policies"}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

/* -------------------------- Net Worth teaser -------------------------- */

function NetWorthTeaserCard() {
  return (
    <Card
      className="group relative overflow-hidden border-gold/25 bg-gradient-to-br from-elevated to-card transition-all hover:border-gold/60"
      data-testid="dashboard-net-worth-card"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-gold/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100"
      />
      <CardContent className="relative flex h-full flex-col gap-4 p-6">
        <SectionHeading icon={IndianRupee} label="Net Worth" gold />
        <p className="flex-1 text-sm text-muted">
          One dashboard for every rupee. Track real estate, mutual funds, ETFs, bonds, FDs,
          crypto, gold and more — separated by category so you can see your allocation at
          a glance.
        </p>
        <Link
          to="/app/net-worth"
          data-testid="dashboard-net-worth-cta"
          className="mt-auto inline-flex items-center gap-1 text-sm font-medium text-gold hover:underline"
        >
          Open my balance sheet
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </CardContent>
    </Card>
  );
}

/* --------------------------------- Section heading ---------------------------------- */

function SectionHeading({ icon: Icon, label, gold = false }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className={`inline-flex h-9 w-9 items-center justify-center rounded-lg border ${
          gold ? "border-gold/40 bg-gold/10 text-gold" : "border-border bg-elevated text-muted"
        }`}
      >
        <Icon className="h-4 w-4" />
      </div>
      <span className="font-serif text-lg text-text">{label}</span>
    </div>
  );
}
